import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import {
  Page,
  Alert,
  Badge,
  Button,
  EmptyState,
  InlineLoading,
  KPI,
  KPIGrid,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { formatCurrency, formatDateTime } from "@marekl/adminer/format";
import { toApiDate } from "@marekl/adminer/datetime";
import {
  useDailyOverview,
  downloadDailyReport,
  type DailySoldVoucher,
} from "../../lib/queries/reports";
import { businessToday } from "../../lib/businessDay";
import { aggregateLatestRides, type AggregatedRide } from "../../lib/aggregateRides";
import styles from "./DailyOverviewPage.module.css";

export function DailyOverviewPage() {
  const overview = useDailyOverview();
  useSetTopbarCrumbs([{ label: "Přehled dne" }]);

  const rides = aggregateLatestRides(overview.data?.rides ?? []);
  const sold = overview.data?.vouchers_sold ?? [];

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function handleDownloadDailyReport() {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadDailyReport(toApiDate(businessToday())!);
    } catch {
      setDownloadError("Stažení denního reportu se nezdařilo. Zkuste to prosím znovu.");
    } finally {
      setDownloading(false);
    }
  }

  const rideColumns: Column<AggregatedRide>[] = [
    {
      key: "time",
      header: "Čas",
      render: (r) => <Text variant="mono">{formatDateTime(r.time)}</Text>,
      tdClass: styles.colTime,
    },
    {
      key: "customer",
      header: "Zákazník",
      primary: true,
      render: (r) => (
        <>
          <Link to={`/vouchers/${r.voucher_id}`}>{r.customer_name || "—"}</Link>
          {r.cancelled && <> <Badge variant="err">stornováno</Badge></>}
        </>
      ),
    },
    {
      key: "type",
      header: "Typ",
      render: (r) => r.voucher_type?.name ?? "—",
    },
    {
      key: "count",
      header: "Jízd",
      render: (r) => <Text variant="mono">×{r.count}</Text>,
      tdClass: styles.colCount,
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <Table.Actions>
          <Table.Action to={`/vouchers/${r.voucher_id}`}>Otevřít</Table.Action>
        </Table.Actions>
      ),
    },
  ];

  const voucherColumns: Column<DailySoldVoucher>[] = [
    {
      key: "time",
      header: "Čas",
      render: (v) => <Text variant="mono">{formatDateTime(v.created_at)}</Text>,
      tdClass: styles.colTime,
    },
    {
      key: "customer",
      header: "Zákazník",
      primary: true,
      render: (v) => (
        <Link to={`/vouchers/${v.id}`}>{v.customer_name || "—"}</Link>
      ),
    },
    {
      key: "type",
      header: "Typ",
      render: (v) => v.voucher_type?.name ?? "—",
    },
    {
      key: "source",
      header: "Zdroj",
      render: (v) =>
        v.source === "eshop" ? (
          <Badge variant="info">e-shop</Badge>
        ) : (
          <Badge variant="muted">recepce</Badge>
        ),
    },
    {
      key: "price",
      header: "Cena",
      render: (v) => <Text variant="mono">{formatCurrency(v.sale_price_czk)}</Text>,
      tdClass: styles.colPrice,
    },
    {
      key: "barcode",
      header: "Kód",
      render: (v) => <Text variant="mono">{v.barcode}</Text>,
    },
    {
      key: "actions",
      header: "",
      render: (v) => (
        <Table.Actions>
          <Table.Action to={`/vouchers/${v.id}`}>Otevřít</Table.Action>
        </Table.Actions>
      ),
    },
  ];

  return (
    <Page.Content>
      <Page.Header
        title="Přehled dne"
        subtitle={`souhrn dne ${businessToday().format("D. M. YYYY")}`}
        subtitleVariant="default"
        actions={
          <Button
            size="sm"
            variant="default"
            onClick={handleDownloadDailyReport}
            loading={downloading}
          >
            <FileText size={14} />
            Denní report
          </Button>
        }
      />

      {downloadError && <Alert variant="err">{downloadError}</Alert>}

      <KPIGrid>
        <KPI
          label="Dnes jízd"
          value={overview.data ? overview.data.rides_today : "—"}
        />
        <KPI
          label="Odbavení zákazníci"
          value={overview.data ? overview.data.customers_today : "—"}
          delta="unikátních voucherů"
        />
        <KPI
          label="Voucherů prodáno dnes"
          value={overview.data ? overview.data.vouchers_sold_today : "—"}
          delta="recepce + e-shop"
        />
        <KPI
          label="Certifikátů objednáno dnes"
          value={overview.data ? overview.data.certificates_ordered_today : "—"}
          delta="e-shop objednávky"
        />
      </KPIGrid>

      <Page.SectionHeader
        title="Jízdy dne"
        meta={overview.data ? `${rides.length} záznamů` : undefined}
      />
      <Table
        cards
        columns={rideColumns}
        rows={overview.data ? rides : []}
        rowKey={(r) => r.id}
        empty={
          overview.isLoading ? (
            <InlineLoading mono>Načítáme…</InlineLoading>
          ) : overview.isError ? (
            <Alert variant="err">Nepodařilo se načíst jízdy dne.</Alert>
          ) : (
            <EmptyState title="Dnes zatím nebyla zaregistrována žádná jízda." />
          )
        }
      />

      <Page.SectionHeader
        className={styles.secondSection}
        title="Prodané vouchery"
        meta={overview.data ? `${sold.length} voucherů` : undefined}
      />
      <Table
        cards
        columns={voucherColumns}
        rows={overview.data ? sold : []}
        rowKey={(v) => v.id}
        empty={
          overview.isLoading ? (
            <InlineLoading mono>Načítáme…</InlineLoading>
          ) : overview.isError ? (
            <Alert variant="err">Nepodařilo se načíst prodané vouchery.</Alert>
          ) : (
            <EmptyState title="Dnes zatím nebyl prodán žádný voucher." />
          )
        }
      />
    </Page.Content>
  );
}
